// Claude Service for Anthropic integration
// All API calls go through the server proxy for credit tracking

import logger from '../utils/logger';
import { supabase } from '../lib/supabase';
import { optimizeImage, formatFileSize } from '../utils/imageOptimization';
import type { ChatMessage, ConversationContext } from '../types/api';

interface ClaudeResponse {
  content: string;
  context: ConversationContext;
  type?: 'text';
}

class ClaudeService {
  private apiBase: string;

  constructor() {
    this.apiBase = process.env.REACT_APP_API_BASE || (typeof window !== 'undefined' && window.location.hostname === 'localhost' ? 'http://localhost:3001' : '');
    logger.info('[ClaudeService] Initialized - all requests go through server proxy at', this.apiBase);
  }

  /**
   * Check if service is configured
   */
  isConfigured(): boolean {
    // Service is always configured in browser - we use server proxy
    return typeof window !== 'undefined';
  }

  /**
   * Convert any image URL to optimized base64 data URL
   * Automatically resizes and compresses large images
   */
  private async toBase64DataUrl(imageUrl: string): Promise<string | null> {
    try {
      // Use image optimization to resize/compress large images
      const result = await optimizeImage(imageUrl, {
        maxDimension: 2048,  // Claude can handle up to 8K but 2K is plenty
        maxFileSizeMB: 4,    // Keep under 5MB for safe API transfer
        preserveTransparency: false // JPEG compression for smaller size
      });
      
      if (result.wasOptimized) {
        logger.info(`[ClaudeService] Image optimized: ${formatFileSize(result.originalSize)} → ${formatFileSize(result.optimizedSize)}`);
      }
      
      return result.dataUrl;
    } catch (e) {
      logger.warn('[ClaudeService] Failed to optimize image, attempting raw conversion:', e);
      
      // Fallback to raw conversion if optimization fails
      try {
        if (imageUrl.startsWith('data:')) {
          return imageUrl;
        }
        
        if (imageUrl.startsWith('blob:') || imageUrl.startsWith('http') || imageUrl.startsWith('/')) {
          const response = await fetch(imageUrl);
          const blob = await response.blob();
          
          const base64Data: string = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              const result = reader.result;
              if (typeof result === 'string') {
                resolve(result);
              } else {
                reject(new Error('FileReader result is not a string'));
              }
            };
            reader.onerror = (err) => reject(err);
            reader.readAsDataURL(blob);
          });
          
          return base64Data;
        }
        
        return null;
      } catch (fetchErr) {
        logger.warn('[ClaudeService] Failed to fetch image for conversion:', imageUrl, fetchErr);
        return null;
      }
    }
  }

  /**
   * Generate response with conversation context
   */
  async generateResponse(
    prompt: string,
    systemPrompt: string | null = null,
    context: ConversationContext | null = null,
    model: string = 'claude-haiku-3-5'
  ): Promise<ClaudeResponse> {
    const tStart = performance.now();
    if (!prompt || prompt.trim().length === 0) {
      throw new Error('Prompt cannot be empty');
    }

    // Get auth token
    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.id;

    // Prepare context messages
    const contextMessages = await this.buildContextMessages(context);

    logger.debug('[ClaudeService] Sending request:', {
      promptLength: prompt.length,
      model,
      contextMessages: contextMessages.length
    });

    const response = await fetch(`${this.apiBase}/api/claude/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(userId && { 'x-user-id': userId })
      },
      body: JSON.stringify({
        prompt,
        systemPrompt,
        model,
        context: { messages: contextMessages }
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      if (response.status === 402) {
        throw new Error(errorData.message || 'Insufficient credits');
      }
      throw new Error(errorData.error || `Claude API error: ${response.status}`);
    }

    const data = await response.json();
    const elapsed = performance.now() - tStart;
    
    logger.debug('[ClaudeService] Response received:', {
      contentLength: data.content?.length,
      elapsed: `${elapsed.toFixed(0)}ms`
    });

    return {
      content: data.content,
      context: {
        messages: [
          ...contextMessages,
          { role: 'user', content: prompt },
          { role: 'assistant', content: data.content }
        ]
      },
      type: 'text'
    };
  }

  /**
   * Normalize a conversation context into the wire shape the server expects,
   * resolving every image part to a base64 data URL.
   *
   * Shared by generateResponse() and generateResponseStream(). It exists as one
   * method because the streaming path used to carry a truncated copy that kept
   * only `typeof content === 'string'` messages — so every image the node
   * adapter collected from upstream was silently dropped here, one line before
   * the fetch. The node logged "imageContexts: N" and sent none of them.
   */
  private async buildContextMessages(
    context: ConversationContext | null
  ): Promise<ChatMessage[]> {
    const contextMessages: ChatMessage[] = [];
    if (!context?.messages) return contextMessages;

    for (const msg of context.messages) {
      if (typeof msg.content === 'string') {
        contextMessages.push(msg);
        continue;
      }
      if (!Array.isArray(msg.content)) continue;

      // Multimodal turn — resolve each image to base64 so the server never has
      // to fetch a blob:/relative URL it cannot reach.
      const processedContent: any[] = [];
      for (const part of msg.content) {
        if (part.type === 'text') {
          processedContent.push(part);
        } else if (part.type === 'image' && part.imageUrl) {
          const base64 = await this.toBase64DataUrl(part.imageUrl);
          if (base64) {
            processedContent.push({ type: 'image', imageUrl: base64 });
          } else {
            logger.warn('[ClaudeService] Dropping image that could not be encoded:', part.imageUrl?.slice(0, 80));
          }
        }
      }
      if (processedContent.length > 0) {
        contextMessages.push({ role: msg.role, content: processedContent });
      }
    }

    return contextMessages;
  }

  /**
   * Generate streaming response (matches OpenAI pattern with callback)
   */
  async generateResponseStream(
    prompt: string,
    systemPrompt: string | null = null,
    context: ConversationContext | null = null,
    onToken?: (delta: string) => void,
    model: string = 'claude-haiku-3-5'
  ): Promise<ClaudeResponse> {
    if (!prompt || prompt.trim().length === 0) {
      throw new Error('Prompt cannot be empty');
    }

    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.id;

    // Same builder as the non-streaming path — images included.
    const contextMessages = await this.buildContextMessages(context);

    logger.debug('[ClaudeService] Streaming request:', {
      promptLength: prompt.length,
      model,
      contextMessages: contextMessages.length,
      imageMessages: contextMessages.filter(
        (m) => Array.isArray(m.content) && m.content.some((p: any) => p.type === 'image')
      ).length,
    });

    const response = await fetch(`${this.apiBase}/api/claude/chat/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...(userId && { 'x-user-id': userId })
      },
      body: JSON.stringify({
        prompt,
        systemPrompt,
        model,
        context: { messages: contextMessages }
      })
    });

    if (!response.ok || !response.body) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Claude streaming error: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let fullContent = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      
      let idx;
      while ((idx = buffer.indexOf('\n\n')) !== -1) {
        const raw = buffer.slice(0, idx).trim();
        buffer = buffer.slice(idx + 2);
        
        if (!raw) continue;
        if (raw.startsWith(':')) continue; // heartbeat
        
        const lines = raw.split('\n');
        let event: string | null = null;
        let dataStr = '';
        
        for (const line of lines) {
          if (line.startsWith('event:')) event = line.slice(6).trim();
          if (line.startsWith('data:')) dataStr += line.slice(5).trim();
        }
        
        if (!dataStr) continue;
        
        try {
          const payload = JSON.parse(dataStr);
          
          if (event === 'done' && typeof payload.content === 'string') {
            fullContent = payload.content;
          } else if (event === 'error') {
            throw new Error(payload.message || 'Streaming failed');
          } else if (typeof payload.delta === 'string') {
            fullContent += payload.delta;
            onToken?.(payload.delta);
          }
        } catch (err) {
          if (err instanceof Error) {
            throw err;
          }
          // Ignore malformed chunks
        }
      }
    }

    return {
      content: fullContent,
      context: {
        messages: [
          ...contextMessages,
          { role: 'user', content: prompt },
          { role: 'assistant', content: fullContent }
        ]
      },
      type: 'text'
    };
  }
}

const claudeService = new ClaudeService();
export default claudeService;
